import api from '@/lib/api';

/**
 * What a scanned code opens.
 *
 * Ported from `mobile-frontend/src/lib/scanRouting.ts` so the same sticker
 * lands on the same screen whichever device read it. One resolver, two callers:
 * the Scan tab, which has a panel for a refusal, and the board's scan button,
 * which shows the same panel inline.
 */

/**
 * `POST /api/qr/scan` wants `TYPE:ID`. Every printed code is job-card-wise now,
 * so anything without a prefix — a job card number typed in when a sticker is
 * torn — means JOBCARD. `ORDER:` is passed through rather than rewritten: old
 * order stickers are still out on the floor, and the backend naming them is how
 * whoever scanned it gets told to use the new one.
 */
export function normalizeQrPayload(data) {
  const prefix = data.split(':', 1)[0].toUpperCase();
  if (['ORDER', 'JOBCARD', 'JC'].includes(prefix) && data.includes(':')) return data;
  return `JOBCARD:${data}`;
}

// `t()` returns the key itself when it does not know one, so a reason this
// build has no wording for has to be spotted here rather than shown raw.
const TRANSLATED_REASONS = [
  'not_started', 'finished', 'another_machine', 'not_on_this_job', 'wrong_stage',
];

/**
 * Why a scan did not open, said in one screenful. The backend names the process
 * the job is at and the process this account runs, and the reason code picks the
 * sentence, so it can be read in Hindi or Punjabi rather than in English only.
 */
export function describeBlock(error, t) {
  const detail = error?.response?.data?.detail;

  if (detail && typeof detail === 'object' && detail.reason) {
    if (!TRANSLATED_REASONS.includes(detail.reason)) {
      return { title: detail.title, message: detail.message };
    }
    const fill = (text) => text
      .replace(/{here}/g, (detail.current_stages || []).join(' and '))
      .replace(/{yours}/g, (detail.your_stages || []).join(' and '));
    return {
      title: t(`scan.block_title_${detail.reason}`),
      message: fill(t(`scan.block_${detail.reason}`)),
    };
  }

  if (error?.response?.status === 404) {
    return { title: t('scan.cant_open'), message: t('scan.not_found') };
  }
  if (error?.response?.status === 400) {
    return { title: t('scan.cant_open'), message: t('scan.not_a_job_card') };
  }
  return {
    title: t('common.error'),
    message: typeof detail === 'string' ? detail : (error?.message || t('scan.not_found')),
  };
}

/**
 * Open whatever was scanned, or say why it could not be opened.
 *
 * `navigate` is react-router's, and every destination is inside the supervisor's
 * phone shell — a code scanned here must not drop him into the desktop app.
 */
export async function resolveScan(scannedData, navigate, t) {
  const data = (scannedData || '').trim();
  if (!data) return { ok: false, title: t('scan.cant_open'), message: t('scan.not_found') };

  try {
    const res = await api.post('/qr/scan', { qr_data: normalizeQrPayload(data) });
    const result = res.data;

    if (result.type === 'JOBCARD') {
      navigate(`/supervisor/job-card/${result.data.job_card.id}`);
      return { ok: true };
    }
    // A process slip names its own process, so there is nothing left to choose —
    // but running one is the machine operator's screen, not the supervisor's.
    // He is sent to the job card the slip belongs to instead.
    if (result.type === 'PROCESS') {
      const jobCardId = result.data?.job_card?.id || result.data?.job_card_id;
      if (jobCardId) {
        navigate(`/supervisor/job-card/${jobCardId}`);
        return { ok: true };
      }
    }
    return { ok: false, title: t('scan.cant_open'), message: t('scan.not_a_job_card') };
  } catch (e) {
    return { ok: false, ...describeBlock(e, t) };
  }
}
